import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogoRefabric, IconSupport, IconClose } from "icons";
import ThemeToggle from "components/ThemeToggle";
import UserProfileSidebar from "components/SideMenu/UserProfileSidebar";
import { useMenuItems, MenuItem } from "components/SideMenu/useMenuItems";
import NavTree from "components/SideMenu/NavTree";
import { PATHS } from "router/config/paths";
import { AnalyticsEvent } from "utils";
import { confirmNavigation } from "utils/unsavedChangesGuard";
import { lockBodyScroll, unlockBodyScroll } from "utils/bodyScrollLock";

interface Props {
  open: boolean;
  onClose: () => void;
}

const MobileNavDrawer = ({ open, onClose }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { sections, isExactActive, isBranchActive, handleItemClick, isAuthorized } = useMenuItems();

  // Body scroll lock while the drawer is open. Routed through the shared ref-counted util
  // (same lock Modal.tsx uses) so it composes with any other open overlay instead of racing
  // it on the single global <body>. The previous inline capture/restore snapshotted an
  // already-locked <body> when it overlapped another lock and restored position:fixed on
  // close, freezing page scroll on the mobile-shell/tablet window-scroll path (REF-5795).
  useEffect(() => {
    if (!open) return;

    lockBodyScroll();

    return () => {
      unlockBodyScroll();
    };
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKey);

    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  const onItemClick = async (item: MenuItem) => {
    await handleItemClick(item);
    onClose();
  };

  const goHome = async () => {
    if (!(await confirmNavigation())) return;
    navigate(PATHS.HOME);
    onClose();
  };

  const goToAcademy = async () => {
    if (!(await confirmNavigation())) return;
    AnalyticsEvent("nav_academy_click", "", "click");
    navigate(PATHS.ACADEMY_HOME);
    onClose();
  };

  const drawer = (
    <div className={clsx("fixed inset-0 z-[99999] transition-opacity duration-200", open ? "opacity-100" : "pointer-events-none opacity-0")} aria-hidden={!open}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} role="presentation" />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("common:nav.menu_aria")}
        className={clsx(
          "absolute left-0 top-0 h-full w-[280px] max-w-[85vw] flex flex-col bg-black-100 border-r border-black-300 transition-transform duration-200",
          open ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full p-2.5">
          {/* Header: logo + close */}
          <div className="flex items-center gap-2 px-2 py-3 mb-2">
            <LogoRefabric className="text-trueBlack dark:text-white h-5 cursor-pointer" onClick={goHome} />
            <button
              type="button"
              aria-label={t("common:nav.close_menu")}
              className="flex-shrink-0 ml-auto flex-center w-8 h-8 rounded text-trueBlack dark:text-white hover:bg-black-200 transition-colors"
              onClick={onClose}
            >
              <IconClose className="w-[18px] h-[18px]" />
            </button>
          </div>

          {/* Navigation sections */}
          <div className="flex flex-col flex-1 overflow-y-auto">
            <NavTree sections={sections} isExactActive={isExactActive} isBranchActive={isBranchActive} onItemClick={onItemClick} isAuthorized={isAuthorized} />
          </div>

          {/* Bottom section */}
          <div className="mt-auto flex flex-col gap-0.5">
            <button
              className={clsx(
                "group flex items-center w-full gap-2 p-2 rounded cursor-pointer transition-colors",
                isExactActive({ path: PATHS.ACADEMY_HOME }) ? "bg-[#E4E4E4] dark:bg-black-300 dark:text-green text-trueBlack" : "text-trueBlack dark:text-grey hover:bg-black-200"
              )}
              onClick={goToAcademy}
            >
              <IconSupport className="h-[18px] w-[18px] flex-shrink-0" />
              <span className="text-menu">{t("common:nav.academy")}</span>
            </button>

            <ThemeToggle />

            <div className="h-px bg-black-300 my-2" />

            <UserProfileSidebar />
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(drawer, document.body);
};

export default MobileNavDrawer;
